import type { CollisionService } from './collision';
import type { TickInput } from './input';
import type { DerivedTuning, MovementTuning, SurfaceMap } from './tuning';
import { surfaceFor } from './tuning';
import type { V3 } from './vec';
import { v3 } from './vec';

/**
 * The motion model — where the feel lives. Pure: same (state, input, tuning, dt) in, same state
 * out, with collision reached only through the CollisionService seam. Rapier corrects the desired
 * translation; every feel decision (gravity, curves, forgiveness) is made HERE, never by physics.
 *
 * M2 scope: ground locomotion with skid braking, asymmetric-gravity jump with apex hang, variable
 * jump height (release-cut), coyote time, jump buffering, hover (hold jump — the Astro verb, no
 * double jump), and default-on ledge guard. M4 adds moving-platform riding + velocity inheritance
 * and ground-surface tracking (both fed through MoveResult — the sim stays pure). Still to come:
 * wall mechanics (post-pilot).
 */
export type MotionMode = 'ground' | 'air' | 'hover';

export interface MotionState {
  /** Capsule FEET position, meters. */
  pos: V3;
  vel: V3;
  mode: MotionMode;
  grounded: boolean;
  coyoteMsLeft: number;
  jumpBufferMsLeft: number;
  msSinceTakeoff: number;
  jumpCutApplied: boolean;
  /** Remaining hover fuel in ms. Refills instantly (and only) on ground contact. */
  hoverFuelMs: number;
  /** How long jump has been held while airborne & slow-rising — gates hover entry. */
  hoverHoldMs: number;
  /** True on the single tick the ledge guard stopped the character (drives teeter feedback). */
  teetering: boolean;
  /** True while brake-accel is engaged (input opposing velocity at speed) — drives skid feedback. */
  skidding: boolean;
  /** Dodge roll: committed burst; >0 while rolling. */
  rollMsLeft: number;
  rollCooldownMsLeft: number;
  rollDirX: number;
  rollDirZ: number;
  /** Velocity of the platform under our feet (zero when not on one). Inherited once on leaving. */
  platformVel: V3;
  /** Surface tag of the last ground stood on — kept while airborne so LAND reads the fresh one. */
  groundSurface: string;
}

export function createMotionState(pos: V3 = v3(), t?: MovementTuning): MotionState {
  return {
    pos: { ...pos },
    vel: v3(),
    mode: 'ground',
    grounded: true,
    coyoteMsLeft: 0,
    jumpBufferMsLeft: 0,
    msSinceTakeoff: Number.MAX_SAFE_INTEGER,
    jumpCutApplied: false,
    hoverFuelMs: t?.hoverDurationMs ?? 0,
    hoverHoldMs: 0,
    teetering: false,
    skidding: false,
    rollMsLeft: 0,
    rollCooldownMsLeft: 0,
    rollDirX: 0,
    rollDirZ: 0,
    platformVel: v3(),
    groundSurface: 'default',
  };
}

export const isRolling = (s: MotionState): boolean => s.rollMsLeft > 0;

export function stepMotion(
  s: MotionState,
  input: TickInput,
  t: MovementTuning,
  d: DerivedTuning,
  dt: number,
  collide: CollisionService,
  /** Surface physics profiles (content/tuning/surfaces.json). Omit = every surface neutral. */
  surfaces?: SurfaceMap,
): MotionState {
  const dtMs = dt * 1000;

  // Forgiveness bookkeeping (uses last tick's grounded state — that is the point of coyote time).
  const coyote = s.grounded ? t.coyoteMs : Math.max(0, s.coyoteMsLeft - dtMs);
  let buffer = input.jump.pressed ? t.jumpBufferMs : Math.max(0, s.jumpBufferMsLeft - dtMs);

  const vel: V3 = { ...s.vel };
  let mode: MotionMode = s.mode;
  let msSinceTakeoff = Math.min(s.msSinceTakeoff + dtMs, Number.MAX_SAFE_INTEGER);
  let jumpCutApplied = s.jumpCutApplied;
  let coyoteAfterJump = coyote;
  let hoverFuelMs = s.hoverFuelMs;
  let hoverHoldMs = s.hoverHoldMs;
  let platformVel: V3 = { ...s.platformVel };

  // Dodge roll (Hogwarts-style): committed grounded burst toward the stick (or velocity).
  // No steering, no jump during it; the jump buffer keeps ticking so roll→jump chains cleanly.
  let rollMsLeft = Math.max(0, s.rollMsLeft - dtMs);
  let rollCooldownMsLeft = Math.max(0, s.rollCooldownMsLeft - dtMs);
  if (rollMsLeft <= 0 && s.rollMsLeft > 0) rollCooldownMsLeft = t.rollCooldownMs;
  let rollDirX = s.rollDirX;
  let rollDirZ = s.rollDirZ;
  if (
    input.roll.pressed &&
    s.grounded &&
    t.rollDurationMs > 0 &&
    rollMsLeft <= 0 &&
    rollCooldownMsLeft <= 0
  ) {
    let dx = input.move.x;
    let dz = input.move.y;
    let m = Math.hypot(dx, dz);
    if (m < 0.3) {
      dx = s.vel.x;
      dz = s.vel.z;
      m = Math.hypot(dx, dz);
    }
    if (m > 0.3) {
      rollMsLeft = t.rollDurationMs;
      rollDirX = dx / m;
      rollDirZ = dz / m;
      // The dive: a small launch hop so the roll leaves the ground briefly and reads as a
      // forward dive rather than a ground-level flip.
      vel.y = Math.max(vel.y, t.rollLaunchVy);
    }
  }
  const rolling = rollMsLeft > 0;

  // Takeoff: buffered press + (grounded or inside the coyote window). One press, one takeoff —
  // consuming the buffer and zeroing coyote makes double-fires structurally impossible.
  const jumpedThisTick = !rolling && buffer > 0 && (s.grounded || coyote > 0);
  if (jumpedThisTick) {
    vel.y = d.jumpVelocity;
    buffer = 0;
    coyoteAfterJump = 0;
    msSinceTakeoff = 0;
    jumpCutApplied = false;
    mode = 'air';
  }

  // Variable jump height: releasing early cuts upward velocity once, inside a short window.
  if (
    !jumpedThisTick &&
    mode === 'air' &&
    input.jump.released &&
    vel.y > 0 &&
    msSinceTakeoff < t.jumpCutMaxTimeMs &&
    !jumpCutApplied
  ) {
    vel.y *= t.jumpCutVelocityMul;
    jumpCutApplied = true;
  }

  // Hover entry — the Astro verb: keep holding jump once the rise is mostly spent. One analog
  // air resource instead of a discrete double jump.
  if (mode === 'air' && !jumpedThisTick && input.jump.held && vel.y < 1.0) {
    hoverHoldMs += dtMs;
    if (hoverHoldMs >= t.hoverHoldDelayMs && hoverFuelMs > 0) {
      mode = 'hover';
      vel.y += t.hoverKickImpulse; // the "thruster catch" moment
    }
  } else if (mode === 'air') {
    hoverHoldMs = 0;
  }

  // Vertical integration.
  if (mode === 'hover') {
    if (!input.jump.held) {
      mode = 'air';
    } else {
      hoverFuelMs -= dtMs;
      if (hoverFuelMs <= 0) {
        hoverFuelMs = 0;
        mode = 'air';
        vel.y = Math.max(vel.y, -1.0); // expiry never drops you like a stone
      }
    }
  }
  if (mode === 'hover') {
    vel.y -= t.hoverGravity * dt;
    vel.y = Math.min(Math.max(vel.y, t.hoverVyMin), t.hoverVyMax);
  } else {
    let g = vel.y > 0 ? d.gravityRise : d.gravityFall;
    if (Math.abs(vel.y) < t.apexHangVelThreshold) g *= t.apexHangGravityMul;
    vel.y = Math.max(vel.y - g * dt, -t.terminalVelocity);
  }

  // Horizontal: accelerate the XZ velocity vector toward the wish velocity, capped per tick.
  // Surface physics: the ground profile (from LAST tick's contact — one tick stale at 60 Hz is
  // imperceptible) scales grip and target speed. Cutting accel IS cutting grip in this model, so
  // ice needs no special code path: slow spin-up, long glide, wide drifting turns all emerge.
  // Air control is never surface-dependent, and the roll's forced burst stays committed.
  const surf = surfaceFor(surfaces, s.groundSurface);
  const wishMag = Math.min(1, Math.hypot(input.move.x, input.move.y));
  // Sprint raises the TARGET speed and nothing else: same accel, same friction, same surface
  // scaling, same state machine. It deliberately applies in the air too, so a sprint-jump keeps
  // its speed instead of decaying to a walk at the apex. Hover keeps its own ceiling — that mode
  // is a designed hang, not locomotion, and a faster one is a different mechanic.
  const sprintMul = input.sprint.held ? t.sprintSpeedMul : 1;
  const maxSpeed =
    mode === 'hover'
      ? t.hoverMaxSpeed
      : t.maxRunSpeed * sprintMul * (s.grounded ? surf.maxSpeedMul : 1);
  const targetX = input.move.x * maxSpeed;
  const targetZ = input.move.y * maxSpeed;
  const speed = Math.hypot(vel.x, vel.z);

  let accel: number;
  let skidding = false;
  if (mode === 'hover') {
    accel = t.hoverAirAccel;
  } else if (s.grounded) {
    if (wishMag > 0) {
      // Skid: braking hard when input opposes velocity (drives skid anim/VFX via the bus).
      const dot =
        speed > 0.01 ? (input.move.x * vel.x + input.move.y * vel.z) / (wishMag * speed) : 1;
      skidding = !rolling && dot < t.brakeDotThreshold && speed > 0.5 * t.maxRunSpeed;
      accel = (skidding ? t.brakeAccel : t.groundAccel) * surf.frictionMul;
    } else {
      accel = t.groundDecel * surf.frictionMul;
    }
  } else {
    accel = t.airAccel;
  }

  if (rolling) {
    // Committed: constant burst along the roll direction (air included — the launch hop keeps
    // its trajectory); steering and braking are off.
    vel.x = rollDirX * t.rollSpeed;
    vel.z = rollDirZ * t.rollSpeed;
  } else {
    const maxDelta = accel * dt;
    const dx = targetX - vel.x;
    const dz = targetZ - vel.z;
    const deltaLen = Math.hypot(dx, dz);
    if (deltaLen > 0 && (s.grounded || wishMag > 0 || mode === 'hover')) {
      const scale = Math.min(1, maxDelta / deltaLen);
      vel.x += dx * scale;
      vel.z += dz * scale;
    }
  }

  // Platform velocity inheritance (once, at takeoff): added AFTER the horizontal accel block so
  // the takeoff tick's ground decel can't eat the carried speed. All three axes — jumping off a
  // rising elevator launches, a swinging platform slings.
  if (jumpedThisTick) {
    vel.x += platformVel.x;
    vel.y += platformVel.y;
    vel.z += platformVel.z;
    platformVel = v3();
  }

  // Ledge guard (Astro's quiet superpower): walking slowly toward a drop stops at the edge;
  // at speed you commit and run off. Never applies to jumps or rolls (a roll may carry you off).
  let teetering = false;
  if (t.ledgeGuardEnabled && !rolling && s.grounded && mode === 'ground' && wishMag > 0) {
    const moveSpeed = Math.hypot(vel.x, vel.z);
    if (moveSpeed > 0.01 && moveSpeed < t.ledgeGuardSpeedCutoff * t.maxRunSpeed) {
      const dir: V3 = { x: vel.x / moveSpeed, y: 0, z: vel.z / moveSpeed };
      if (!collide.groundAhead(s.pos, dir, t.ledgeGuardProbeDist)) {
        vel.x = 0;
        vel.z = 0;
        teetering = true;
      }
    }
  }

  // Sweep & resolve.
  const desired: V3 = { x: vel.x * dt, y: vel.y * dt, z: vel.z * dt };
  const res = collide.moveCapsule(s.pos, desired);
  const pos: V3 = { x: s.pos.x + res.actual.x, y: s.pos.y + res.actual.y, z: s.pos.z + res.actual.z };

  // Ride: the platform under our feet moved this tick — carry the character with it.
  if (res.grounded && res.platformDelta) {
    pos.x += res.platformDelta.x;
    pos.y += res.platformDelta.y;
    pos.z += res.platformDelta.z;
  }
  if (res.grounded) {
    platformVel = res.platformVel ? { ...res.platformVel } : v3();
  } else if (s.grounded && !jumpedThisTick) {
    // Walked/fell off the platform: inherit its velocity once (takeoff handled above).
    vel.x += platformVel.x;
    vel.y += platformVel.y;
    vel.z += platformVel.z;
    platformVel = v3();
  }
  // Fresh while grounded (BEFORE deriveFeedback reads `next` — LAND fires on the grounded tick);
  // kept while airborne so late feedback still names the last real ground.
  const groundSurface = res.grounded ? res.surface : s.groundSurface;

  if (res.grounded) {
    if (vel.y < 0) vel.y = 0;
    mode = 'ground';
    hoverFuelMs = t.hoverDurationMs; // instant, ground-only refill (readability)
    hoverHoldMs = 0;
  } else if (mode === 'ground') {
    mode = 'air';
  }

  return {
    pos,
    vel,
    mode,
    grounded: res.grounded,
    coyoteMsLeft: coyoteAfterJump,
    jumpBufferMsLeft: buffer,
    msSinceTakeoff,
    jumpCutApplied,
    hoverFuelMs,
    hoverHoldMs,
    teetering,
    skidding,
    rollMsLeft,
    rollCooldownMsLeft,
    rollDirX,
    rollDirZ,
    platformVel,
    groundSurface,
  };
}

/** Frame-budget guard: cap sim ticks per rendered frame (spiral-of-death protection). */
export const FIXED_DT = 1 / 60;
export const MAX_TICKS_PER_FRAME = 5;

export function speedXZ(s: MotionState): number {
  return Math.hypot(s.vel.x, s.vel.z);
}
